import { IFilterParams, IDoesFilterPassParams, IFilterComp } from '@ag-grid-community/core';
import { cancelButtonBg } from '../../styles';
import { PRICING_DAY_FILTER_LABELS, TBD_LABEL } from './deals.util';

export class DayOfWeekFilter implements IFilterComp {
  private params!: IFilterParams;
  private filterChangedCallback!: () => void;
  private selectedDays: Set<string> = new Set();
  private gui!: HTMLElement;

  private readonly allDayLabels: readonly string[] = PRICING_DAY_FILTER_LABELS;

  init(params: IFilterParams): void {
    this.params = params;
    this.filterChangedCallback = params.filterChangedCallback;
    this.selectedDays = new Set(this.allDayLabels);

    this.gui = document.createElement('div');
    this.gui.style.padding = '5px 0';

    this.gui.innerHTML = `
      <div style="margin-bottom: 6px;">
        <label style="display: flex; margin: 8px; align-items: center;">
          <input style="margin-right: 10px;" type="checkbox" data-action="selectAll" checked />
          <strong>Select All</strong>
        </label>
      </div>
      <div style="border-top: 1px solid ${cancelButtonBg};"></div>
      <div>
        ${this.allDayLabels
          .map(
            (day) => `
              <label style="display: flex; margin: 8px; align-items: center;">
                <input type="checkbox" style="margin-right: 10px;" value="${day}" checked />
                ${day}
              </label>
            `,
          )
          .join('')}
      </div>
      <div style="border-top: 1px solid ${cancelButtonBg};"></div>
      <div style="margin-top: 6px; display: flex; justify-content: flex-end;">
        <rapid-button data-action="reset">Reset</rapid-button>
      </div>
    `;

    this.attachCheckboxHandlers();
  }

  private attachCheckboxHandlers(): void {
    const allCheckbox = this.gui.querySelector<HTMLInputElement>('input[data-action="selectAll"]');
    const dayCheckboxes = Array.from(
      this.gui.querySelectorAll<HTMLInputElement>('input[type="checkbox"]:not([data-action])'),
    );

    // Day checkbox change
    dayCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', () => {
        checkbox.checked
          ? this.selectedDays.add(checkbox.value)
          : this.selectedDays.delete(checkbox.value);

        // Update "Select All" checkbox
        allCheckbox!.checked = this.selectedDays.size === this.allDayLabels.length;
        this.filterChangedCallback();
      });
    });

    // Select All checkbox change
    allCheckbox?.addEventListener('change', () => {
      const isChecked = allCheckbox.checked;
      this.selectedDays = isChecked ? new Set(this.allDayLabels) : new Set();

      dayCheckboxes.forEach((checkbox) => {
        checkbox.checked = isChecked;
      });

      this.filterChangedCallback();
    });

    this.gui
      .querySelector<HTMLButtonElement>('[data-action="reset"]')
      ?.addEventListener('click', () => {
        this.selectedDays = new Set(this.allDayLabels);
        this.updateCheckboxes();
        this.filterChangedCallback();
      });
  }

  getGui(): HTMLElement {
    return this.gui;
  }

  doesFilterPass(params: IDoesFilterPassParams): boolean {
    const value = params.data && params.data[this.params.colDef.field];
    const dayLabel = this.getDayLabel(value);
    return this.selectedDays.has(dayLabel);
  }

  isFilterActive(): boolean {
    return this.selectedDays.size !== this.allDayLabels.length;
  }

  getModel(): any {
    return this.isFilterActive() ? { values: Array.from(this.selectedDays) } : null;
  }

  setModel(model: any): void {
    this.selectedDays = new Set(model?.values || this.allDayLabels);
    this.updateCheckboxes();
  }

  destroy(): void {
    this.gui.remove();
  }

  private updateCheckboxes(): void {
    const dayCheckboxes = Array.from(
      this.gui.querySelectorAll<HTMLInputElement>('input[type="checkbox"]:not([data-action])'),
    );

    dayCheckboxes.forEach((checkbox) => {
      checkbox.checked = this.selectedDays.has(checkbox.value);
    });

    const allCheckbox = this.gui.querySelector<HTMLInputElement>('input[data-action="selectAll"]');
    allCheckbox!.checked = this.selectedDays.size === this.allDayLabels.length;
  }

  private getDayLabel(value: any): string {
    if (!value) return TBD_LABEL;
    const date = new Date(value);
    // Use UTC to avoid local timezone shifting the calendar day.
    const day = date.getUTCDay(); // 0 = Sunday, 1 = Monday, ...
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[day] || TBD_LABEL;
  }
}
